import {
    DashboardOutlined,
    EmailOutlined,
    OpenInNew,
    SmsOutlined,
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import { Notifications } from '../object-actions/types/types';
import {
    formatFullDate,
    formatNotificationTimestamp,
    formatNotificationTypeLabel,
    formatRelativeTime,
    getChannelLabel,
    getNotificationStatusColor,
    getNotificationStatusLabel,
    getPriorityColor,
    getPriorityLabel,
    isNotificationSeen,
} from '../utils/notificationUtils';

interface NotificationFullCardProps {
    notification: Notifications;
}

const CHANNEL_ICONS = {
    dashboard: DashboardOutlined,
    email: EmailOutlined,
    sms: SmsOutlined,
} as const;

const NotificationFullCard: React.FC<NotificationFullCardProps> = ({
    notification,
}) => {
    const isInternalLink = notification.link?.startsWith('/');
    const hasLink = !!notification.link;
    const ChannelIcon = CHANNEL_ICONS[notification.channel] ?? DashboardOutlined;
    const seen = isNotificationSeen(notification);
    const statusLabel = getNotificationStatusLabel(
        notification.channel,
        notification.seen_at,
        notification.delivery_status,
    );
    const statusColor = getNotificationStatusColor(
        notification.channel,
        notification.seen_at,
        notification.delivery_status,
    );
    const showPriority = notification.priority === 'high' || notification.priority === 'urgent';
    const isExpired = notification.expires
        ? new Date(notification.expires) < new Date()
        : false;
    const timestampTitle = [
        `Created ${formatFullDate(notification.created_at)}`,
        notification.sent_at ? `Sent ${formatFullDate(notification.sent_at)}` : null,
        notification.expires ? `Expires ${formatFullDate(notification.expires)}` : null,
    ].filter(Boolean).join(' · ');

    const cardProps = hasLink
        ? isInternalLink
            ? { component: Link, to: notification.link }
            : { component: 'a' as const, href: notification.link, target: '_blank', rel: 'noopener noreferrer' }
        : {};

    return (
        <Card
            {...cardProps}
            variant="outlined"
            sx={{
                display: 'flex',
                mb: 1,
                overflow: 'hidden',
                opacity: seen ? 0.92 : 1,
                cursor: hasLink ? 'pointer' : 'default',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': hasLink ? {
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                } : undefined,
            }}
        >
            <Box
                sx={{
                    width: 3,
                    flexShrink: 0,
                    bgcolor: seen ? 'divider' : 'primary.main',
                }}
            />
            <CardContent sx={{ flex: 1, py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                        sx={{
                            mt: 0.25,
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        <ChannelIcon sx={{ fontSize: 16 }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: seen ? 400 : 600,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {notification.message}
                            </Typography>

                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                                {hasLink && (
                                    <Tooltip title={isInternalLink ? 'Open in app' : 'Open external link'}>
                                        <OpenInNew sx={{ fontSize: 14, color: 'primary.main' }} />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            alignItems="center"
                            sx={{ mt: 0.75 }}
                        >
                            <Chip label={getChannelLabel(notification.channel)} size="small" variant="outlined" />
                            {notification.notification_type && (
                                <Chip
                                    label={formatNotificationTypeLabel(notification.notification_type)}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" />
                            {showPriority && (
                                <Chip
                                    label={getPriorityLabel(notification.priority)}
                                    size="small"
                                    color={getPriorityColor(notification.priority)}
                                />
                            )}
                            {notification.expires && (
                                <Chip
                                    label={isExpired ? 'Expired' : `Expires ${formatRelativeTime(notification.expires)}`}
                                    size="small"
                                    color={isExpired ? 'error' : 'warning'}
                                    variant="outlined"
                                />
                            )}
                            {notification.sent_at && notification.channel !== 'dashboard' && (
                                <Typography variant="caption" color="text.secondary">
                                    Sent {formatNotificationTimestamp(notification.sent_at)}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default NotificationFullCard;
